import React from 'react';
import { getAvailableThemes, getThemeDefaultHighlightColor } from 'components/quote_builder_widget/themes';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';

const FONT_FAMILIES = [
  { value: '', label: 'Default (System)' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System UI' },
];

const AppearanceSection = ({
  selectedTheme,
  setSelectedTheme,
  highlightColor,
  setHighlightColor,
  submitButtonColor,
  setSubmitButtonColor,
  backgroundColor,
  setBackgroundColor,
  fontFamily,
  setFontFamily,
  customCss,
  supportsCustomCss,
  onOpenCssEditor,
}) => {
  const availableThemes = getAvailableThemes();

  return (
    <div>
      <h4 className={styles.sectionHeader}>
        {t('quote_builder_widget.section_appearance')}
      </h4>

      {/* Theme */}
      <div className={styles.fieldGroup}>
        <label className={styles.label}>{t('quote_builder_widget.theme')}</label>
        <p className={styles.helpText}>{t('quote_builder_widget.theme_info')}</p>
        <select
          className={`form-control ${styles.themeSelect}`}
          value={selectedTheme}
          onChange={(event) => setSelectedTheme(event.target.value)}
        >
          {availableThemes.map((themeName) => (
            <option key={themeName} value={themeName}>
              {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {supportsCustomCss && (
        <>
          {/* Highlight Color */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('quote_builder_widget.highlight_color')}</label>
            <p className={styles.helpText}>{t('quote_builder_widget.highlight_color_info')}</p>
            <div className={styles.colorInputContainer}>
              <input
                type="color"
                value={highlightColor}
                onChange={(event) => setHighlightColor(event.target.value)}
                className={styles.colorInput}
              />
              <input
                type="text"
                className={`form-control ${styles.hexInput}`}
                value={highlightColor}
                onChange={(event) => setHighlightColor(event.target.value)}
              />
              {highlightColor !== getThemeDefaultHighlightColor(selectedTheme) && (
                <button
                  type="button"
                  onClick={() => setHighlightColor(getThemeDefaultHighlightColor(selectedTheme))}
                  className={styles.resetButton}
                  title={t('quote_builder_widget.reset_to_theme_default')}
                >
                  {t('quote_builder_widget.reset')}
                </button>
              )}
            </div>
          </div>

          {/* Submit Button Color */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('quote_builder_widget.submit_button_color')}</label>
            <p className={styles.helpText}>{t('quote_builder_widget.submit_button_color_info')}</p>
            <div className={styles.colorInputContainer}>
              <input
                type="color"
                value={submitButtonColor || highlightColor}
                onChange={(event) => setSubmitButtonColor(event.target.value)}
                className={styles.colorInput}
              />
              <input
                type="text"
                className={`form-control ${styles.hexInput}`}
                value={submitButtonColor || highlightColor}
                onChange={(event) => setSubmitButtonColor(event.target.value)}
                placeholder={highlightColor}
              />
              {submitButtonColor && (
                <button
                  type="button"
                  onClick={() => setSubmitButtonColor('')}
                  className={styles.resetButton}
                  title={t('quote_builder_widget.reset_to_highlight_color')}
                >
                  {t('quote_builder_widget.reset')}
                </button>
              )}
            </div>
          </div>

          {/* Background Color */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('quote_builder_widget.background_color')}</label>
            <p className={styles.helpText}>{t('quote_builder_widget.background_color_info')}</p>
            <div className={styles.colorInputContainer}>
              <input
                type="color"
                value={backgroundColor}
                onChange={(event) => setBackgroundColor(event.target.value)}
                className={styles.colorInput}
              />
              <input
                type="text"
                className={`form-control ${styles.hexInput}`}
                value={backgroundColor}
                onChange={(event) => setBackgroundColor(event.target.value)}
              />
              {backgroundColor !== DEFAULT_BACKGROUND_COLOR && (
                <button
                  type="button"
                  onClick={() => setBackgroundColor(DEFAULT_BACKGROUND_COLOR)}
                  className={styles.resetButton}
                  title={t('quote_builder_widget.reset_to_default')}
                >
                  {t('quote_builder_widget.reset')}
                </button>
              )}
            </div>
          </div>

          {/* Font Family */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('quote_builder_widget.font_family')}</label>
            <p className={styles.helpText}>{t('quote_builder_widget.font_family_info')}</p>
            <select
              className={`form-control ${styles.fontSelect}`}
              value={fontFamily}
              onChange={(event) => setFontFamily(event.target.value)}
            >
              {FONT_FAMILIES.map((font) => (
                <option key={font.value} value={font.value} style={{ fontFamily: font.value || 'inherit' }}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom CSS */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('quote_builder_widget.custom_css')}</label>
            <p className={styles.helpText}>{t('quote_builder_widget.custom_css_info')}</p>
            <button
              type="button"
              className="btn simple-btn"
              onClick={onOpenCssEditor}
            >
              <i className="fa fa-code" aria-hidden="true"></i>{' '}
              {customCss ? t('quote_builder_widget.edit_css') : t('quote_builder_widget.add_custom_css')}
            </button>
            {customCss && (
              <span className={styles.cssAppliedIndicator}>
                <i className="fa fa-check-circle" aria-hidden="true"></i>{' '}
                {t('quote_builder_widget.custom_css_applied')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AppearanceSection;
